/**
 * Consent step of the Connect Splyt page: what the assistant gets, Allow or
 * Not now. Shows a required name field when the profile has none, because the
 * assistant's receipts carry that name to the person's friends.
 */
import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { brand } from '../../style/splytBrand';
import { normalizeJoinName } from '../join/inviteHelpers';
import useConnectStyles from './connectStyles';
import ConnectShell, { useIsDesktop } from './ConnectShell';
import { approveConnect, denyConnect } from './connectClient';
import { assistantName, displayPhoneFor } from './connectHelpers';

const NAME_MAX_LENGTH = 40;

function ScopeMark() {
  const classes = useConnectStyles();
  return (
    <span className={classes.scopeMark} aria-hidden="true">
      <svg width="9" height="9" viewBox="0 0 10 10" fill="none">
        <path
          d="M1.5 5.2l2.4 2.3 4.6-5"
          stroke={brand.surface}
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </span>
  );
}

export default function ConnectConsentStep({ request, user, needsName, onGone }) {
  const classes = useConnectStyles();
  const isDesktop = useIsDesktop();
  const inputRef = useRef(null);

  const [name, setName] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');

  const client = assistantName(request.clientName);
  const signedInAs = displayPhoneFor(user && user.phoneNumber);
  const cleanedName = normalizeJoinName(name);
  const canAllow = !needsName || cleanedName !== '';

  useEffect(() => {
    if (needsName && inputRef.current) inputRef.current.focus();
  }, [needsName]);

  const leave = (redirectUrl) => {
    window.location.assign(redirectUrl);
  };

  const fail = (err) => {
    console.error('[connect] request failed:', err);
    if (err && err.gone) {
      onGone();
      return;
    }
    setError((err && err.message) || "Couldn't reach Splyt. Check your connection and try again.");
    setBusy('');
  };

  const handleAllow = async () => {
    if (!canAllow || busy) return;
    setBusy('allow');
    setError('');
    try {
      const redirectUrl = await approveConnect(
        request.requestId,
        needsName ? cleanedName : undefined,
        request.clientName,
      );
      leave(redirectUrl);
    } catch (err) {
      fail(err);
    }
  };

  const handleDeny = async () => {
    if (busy) return;
    setBusy('deny');
    setError('');
    try {
      const redirectUrl = await denyConnect(request.requestId, request.clientName);
      leave(redirectUrl);
    } catch (err) {
      fail(err);
    }
  };

  const content = (
    <>
      <h2 className={`${classes.title} ${isDesktop ? classes.titleDesktop : classes.titleMobile}`}>
        Connect Splyt to {client}
      </h2>
      {request.redirectHost && (
        <p className={classes.hostLine}>
          You will go back to <span className={classes.hostName}>{request.redirectHost}</span>.
        </p>
      )}
      {signedInAs && (
        <p className={classes.signedInLine}>Signed in as {signedInAs}</p>
      )}

      {request.scopes.length > 0 && (
        <div className={classes.scopeWell}>
          <p className={classes.scopeHeading}>{client} will be able to</p>
          <ul className={classes.scopeList}>
            {request.scopes.map((scope, index) => (
              <li key={scope.id || index} className={classes.scopeItem}>
                <ScopeMark />
                <span>{scope.description}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {needsName && (
        <>
          <label className={classes.fieldLabel} htmlFor="connect-name-input">
            Your name
          </label>
          <div
            className={`${classes.inputWrapper} ${
              isFocused ? classes.inputWrapperFocused : classes.inputWrapperDefault
            }`}
          >
            {/* enterkeyhint is lowercase on purpose: React 16 passes unknown
                lowercase attributes straight through but warns on camelCase ones. */}
            <input
              id="connect-name-input"
              ref={inputRef}
              type="text"
              autoComplete="name"
              // eslint-disable-next-line react/no-unknown-property
              enterkeyhint="done"
              maxLength={NAME_MAX_LENGTH}
              className={classes.textInput}
              placeholder="Your name"
              value={name}
              onChange={(e) => setName(e.target.value.slice(0, NAME_MAX_LENGTH))}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleAllow();
              }}
              onFocus={() => setIsFocused(true)}
              onBlur={() => setIsFocused(false)}
              disabled={busy !== ''}
            />
          </div>
          <p className={classes.fieldHint}>Your friends see it on the receipt.</p>
        </>
      )}

      {error && <div className={classes.errorMessage} role="alert">{error}</div>}
    </>
  );

  const footer = (
    <>
      <button
        type="button"
        className={classes.primaryButton}
        disabled={!canAllow || busy !== ''}
        onClick={handleAllow}
        // iOS fires blur before click: without this the first tap on the pinned
        // bar only closes the keyboard and the button slides out from under the finger.
        onMouseDown={(e) => e.preventDefault()}
      >
        {busy === 'allow' ? 'Connecting...' : 'Allow'}
      </button>
      <button
        type="button"
        className={classes.textButton}
        disabled={busy !== ''}
        onClick={handleDeny}
        onMouseDown={(e) => e.preventDefault()}
      >
        Not now
      </button>
    </>
  );

  return <ConnectShell footer={footer}>{content}</ConnectShell>;
}

ConnectConsentStep.propTypes = {
  request: PropTypes.shape({
    requestId: PropTypes.string.isRequired,
    clientName: PropTypes.string,
    redirectHost: PropTypes.string,
    scopes: PropTypes.arrayOf(
      PropTypes.shape({ id: PropTypes.string, description: PropTypes.string }),
    ).isRequired,
  }).isRequired,
  user: PropTypes.shape({
    uid: PropTypes.string,
    phoneNumber: PropTypes.string,
  }),
  needsName: PropTypes.bool,
  onGone: PropTypes.func.isRequired,
};

ConnectConsentStep.defaultProps = {
  user: null,
  needsName: false,
};
